import { useEffect } from "react";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useAtomValue } from "jotai";

import { AuthorizationContextProvider } from "@/context/AuthorizationProvider";
import { CurrentUserContextProvider } from "@/context/CurrentUserProvider";
import { userRoleAtom } from "@/stores/atoms";
import { useCurrentUserType, useIsAuthenticated } from "@/stores/authStore";

export const AuthedLayout = () => {
  const navigate = useNavigate();

  const userRole = useAtomValue(userRoleAtom);

  const currentUserType = useCurrentUserType();

  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    // When isAuthenticated changes and the user is not authenticated,
    // redirect them to the redirect path
    if (isAuthenticated === false && !location.pathname.includes("/login")) {
      void navigate({
        to: "/login",
        search: {
          redirect: location.pathname.includes("/logout")
            ? undefined
            : location.href,
        },
      });
    }

    if (location.pathname === "/logout") {
      return;
    }

    if (
      currentUserType === "STAFF" &&
      userRole !== "INSTRUCTOR" &&
      !location.pathname.startsWith("/admin")
    ) {
      void navigate({
        to: "/admin",
      });
    } else if (
      userRole === "INSTRUCTOR" &&
      !location.pathname.startsWith("/instructor")
    ) {
      void navigate({
        to: "/instructor",
      });
    } else if (
      currentUserType === "STUDENT" &&
      !location.pathname.startsWith("/student")
    ) {
      void navigate({
        to: "/student",
      });
    }
  }, [isAuthenticated, userRole, currentUserType, navigate]);

  return (
    <CurrentUserContextProvider>
      <AuthorizationContextProvider>
        <Outlet />
      </AuthorizationContextProvider>
    </CurrentUserContextProvider>
  );
};

export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
  beforeLoad: ({ context }) => {
    if (!context.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href.includes("/logout")
            ? undefined
            : location.href,
        },
      });
    }
  },
});
